import { formatDate } from "@/core/utils/date";
import { readTabSeparatedClipboardRows } from "@/payroll/ai-script/ai-script-spreadsheet-viewer/clipboard-text";
import type {
  EnrichedPayrollPeriodNote,
  EnrichedPayrollPeriodNotes,
  PayrollNoteGridPreview,
  PayrollPeriodNote,
  PayrollPeriodNotes,
} from "./types";

const PREVIEW_ROW_LIMIT = 30;
const PREVIEW_COLUMN_LIMIT = 12;
const STATUS_LABELS = {
  open: "Open",
  addressed: "Addressed",
  forwarded: "Sent to next open period",
} satisfies Record<PayrollPeriodNote["status"], string>;

export const createNoteGridPreview = (text: string): PayrollNoteGridPreview => {
  const previewRows: string[][] = [];
  let rowCount = 0;
  let columnCount = 0;
  for (const row of readTabSeparatedClipboardRows(text)) {
    rowCount += 1;
    columnCount = Math.max(columnCount, row.length);
    if (previewRows.length < PREVIEW_ROW_LIMIT) {
      previewRows.push(row.slice(0, PREVIEW_COLUMN_LIMIT));
    }
  }
  const previewColumnCount = Math.min(columnCount, PREVIEW_COLUMN_LIMIT);
  return {
    previewRows: previewRows.map((row) =>
      Array.from(
        { length: previewColumnCount },
        (_, index) => row[index] ?? "",
      ),
    ),
    rowCount,
    columnCount,
    hasMoreCells:
      rowCount > PREVIEW_ROW_LIMIT || columnCount > PREVIEW_COLUMN_LIMIT,
  };
};

const enrichNote = (note: PayrollPeriodNote): EnrichedPayrollPeriodNote => ({
  ...note,
  authorLabel: note.author_name || "Payroll user",
  sourcePeriodLabel: formatDate(note.source_period_start),
  periodLabel: formatDate(note.period_start),
  statusLabel: STATUS_LABELS[note.status],
  resolvedLabel: note.resolved_by_name
    ? `${note.resolved_by_name}, ${formatDate(note.resolved_at)}`
    : null,
});

export const enrichPeriodNotes = (
  data: PayrollPeriodNotes,
): EnrichedPayrollPeriodNotes => {
  const openNotes: EnrichedPayrollPeriodNote[] = [];
  const reviewedNotes: EnrichedPayrollPeriodNote[] = [];
  for (const note of data.notes) {
    (note.status === "open" ? openNotes : reviewedNotes).push(enrichNote(note));
  }
  return {
    openNotes,
    reviewedNotes,
    sentNotes: data.sent_notes.map(enrichNote),
    nextPeriod: data.next_period,
  };
};

const escapeCell = (value: string) =>
  value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;");

export const copyNoteGrid = async (text: string) => {
  if (typeof ClipboardItem === "undefined") {
    await navigator.clipboard.writeText(text);
    return;
  }
  const htmlRows: string[] = [];
  for (const row of readTabSeparatedClipboardRows(text)) {
    htmlRows.push(
      `<tr>${row.map((cell) => `<td style="white-space:pre-wrap">${escapeCell(cell)}</td>`).join("")}</tr>`,
    );
  }
  const html = `<table>${htmlRows.join("")}</table>`;
  await navigator.clipboard.write([
    new ClipboardItem({
      "text/plain": new Blob([text], { type: "text/plain" }),
      "text/html": new Blob([html], { type: "text/html" }),
    }),
  ]);
};
